import { ref } from 'vue';

export function usePagination(options = {}) {
  const { perPage: itemsPerPage = 72, scrollTarget = null } = options;

  const currentPage = ref(1);
  const perPage = ref(itemsPerPage);
  const isChangingPage = ref(false);

  const paginate = (items) => {
    const total = items.length;
    const totalPages = Math.ceil(total / perPage.value);
    const startIndex = (currentPage.value - 1) * perPage.value;
    const endIndex = startIndex + perPage.value;
    const paginatedItems = items.slice(startIndex, endIndex);

    return {
      items: paginatedItems,
      currentPage: currentPage.value,
      totalPages,
      total,
      hasNextPage: currentPage.value < totalPages,
      hasPreviousPage: currentPage.value > 1,
      startIndex,
      endIndex: Math.min(endIndex, total),
    };
  };

  const goToPage = (pageNumber) => {
    if (pageNumber === currentPage.value || isChangingPage.value) {
      return;
    }

    isChangingPage.value = true;
    currentPage.value = pageNumber;

    if (scrollTarget) {
      requestAnimationFrame(() => {
        const element = document.querySelector(scrollTarget);
        if (element) {
          const yOffset = -100;
          const y =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
        isChangingPage.value = false;
      });
    } else {
      isChangingPage.value = false;
    }
  };

  const nextPage = (totalPages) => {
    if (currentPage.value < totalPages && !isChangingPage.value) {
      goToPage(currentPage.value + 1);
    }
  };

  const previousPage = () => {
    if (currentPage.value > 1 && !isChangingPage.value) {
      goToPage(currentPage.value - 1);
    }
  };

  const resetPagination = () => {
    currentPage.value = 1;
    isChangingPage.value = false;
  };

  return {
    currentPage,
    perPage,
    isChangingPage,
    paginate,
    goToPage,
    nextPage,
    previousPage,
    resetPagination,
  };
}
